// @flow
import { useEffect, useRef, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useHasPermission from './useHasPermission';

type WidgetConfig = {
  organizationId: string,
  agentId: string,
  bgColor?: string,
  textColor?: string,
  horizontalPosition?: 'left' | 'right',
  verticalPosition?: 'top' | 'bottom',
  signedUserData?: string,
  unsignedUserData?: { [key: string]: any },
};

type UseMavenWidgetOptions = {
  enabled?: boolean,
  requiredPermission?: string,
  config?: $Shape<WidgetConfig>,
  currentUser?: ?Object,
};

/**
 * Custom hook to initialize and manage the Maven AGI chat widget
 *
 * @param {UseMavenWidgetOptions} options - Configuration options
 * @returns {Object} Object containing widget state and utility functions
 */
const useMavenWidget = (
  options: UseMavenWidgetOptions = {},
): { isEnabled: boolean, isInitialized: boolean } => {
  const {
    enabled = true,
    requiredPermission = null,
    config = {},
    currentUser = null,
  } = options;

  const { mavenAgi } = useFlags();
  const hasPermission = useHasPermission(requiredPermission);
  const widgetInitialized = useRef(false);

  // Determine if widget should be loaded
  const shouldLoadWidget = useMemo(() => {
    // Check if user is logged in
    if (!currentUser) return false;

    // Check feature flag
    if (!mavenAgi || !enabled) return false;

    // Check permissions if required
    if (requiredPermission && !hasPermission) return false;

    return true;
  }, [currentUser, mavenAgi, enabled, requiredPermission, hasPermission]);

  // Initialize widget
  useEffect(() => {
    if (!shouldLoadWidget) {
      // Hide widget if conditions are no longer met
      if (
        widgetInitialized.current &&
        window.Maven &&
        window.Maven.ChatWidget
      ) {
        try {
          // Hide the widget by removing it from DOM
          const widgetElement = document.querySelector(
            '[id^="maven-chat-widget"]',
          );
          if (widgetElement) {
            widgetElement.style.display = 'none';
          }
        } catch (error) {
          console.error('Failed to hide Maven widget:', error);
        }
      }
      return;
    }

    if (widgetInitialized.current) {
      // Show widget again if it was previously hidden
      if (window.Maven && window.Maven.ChatWidget) {
        try {
          const widgetElement = document.querySelector(
            '[id^="maven-chat-widget"]',
          );
          if (widgetElement) {
            widgetElement.style.display = '';
          }
        } catch (error) {
          console.error('Failed to show Maven widget:', error);
        }
      }
      return;
    }

    // Wait for window.Maven to be available
    const initWidget = () => {
      if (window.Maven && window.Maven.ChatWidget) {
        try {
          const widgetConfig: WidgetConfig = {
            organizationId: 'fortressos',
            agentId: 'support',
            textColor: 'white',
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            ...config,
          };

          window.Maven.ChatWidget.load(widgetConfig);
          widgetInitialized.current = true;
        } catch (error) {
          console.error('Failed to initialize Maven widget:', error);
        }
      }
    };

    // If script is already loaded
    if (window.Maven) {
      initWidget();
    } else {
      // Wait for script to load
      window.addEventListener('load', initWidget);
      return () => window.removeEventListener('load', initWidget);
    }
  }, [shouldLoadWidget, config]);

  return {
    isEnabled: shouldLoadWidget,
    isInitialized: widgetInitialized.current,
  };
};

export default useMavenWidget;
